var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector } from '@ngxs/store';
import { GalleryStateModel } from './gallery.state.model';
import { GallerySearchMode } from '../models/gallery-search-mode.enum';
import { GalleryViewMode } from '../models/gallery-view-mode.enum';
import { GalleryActions } from './gallery.actions';
import { exploreMenu } from 'src/app/explore/data/explore-menu';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { of, forkJoin, Observable } from 'rxjs';
import { StudioOptionsModel } from '../models/studio-options.model';
import { ModulesService } from 'src/app/modules/modules.service';
import { finalize, map } from 'rxjs/operators';
import { ExploreService } from 'src/app/explore/explore.service';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { BookmarksService } from 'src/app/bookmarks/bookmarks.service';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { sortModels } from 'src/app/bookmarks/models/bookmark-sort.model';
import { bookmarkSortOrders, bookmarkSortTitleAsc } from 'src/app/bookmarks/data/bookmark-sort';
const initialState = {
    loading: true,
    searchMode: GallerySearchMode.Query,
    viewMode: GalleryViewMode.Cards,
    sort: bookmarkSortTitleAsc,
    sortOptions: bookmarkSortOrders,
    studioOptions: [],
    studioQuery: '',
    studioCategoryId: 'category||',
    searchResults: [],
    checkedResults: []
};
let GalleryState = class GalleryState {
    constructor(organizationsService, modulesService, exploreService, bookmarksService) {
        this.organizationsService = organizationsService;
        this.modulesService = modulesService;
        this.exploreService = exploreService;
        this.bookmarksService = bookmarksService;
    }
    /**
     * Selectors
     */
    static getLoading(state) {
        return state.loading;
    }
    static getSearchMode(state) {
        return state.searchMode;
    }
    static getViewMode(state) {
        return state.viewMode;
    }
    static getSort(state) {
        return state.sort;
    }
    static getSortOptions(state) {
        return state.sortOptions;
    }
    static getStudioOptions(state) {
        return state.studioOptions;
    }
    static getSearchResults(state) {
        const { searchResults, sort } = state;
        const { ascending, fields } = sort;
        return sortModels(searchResults, fields, ascending);
    }
    static getStudioCategoryId(state) {
        return state.studioCategoryId;
    }
    static getStudioQuery(state) {
        return state.studioQuery;
    }
    /**
     * Actions
     */
    setSearchMode(context, action) {
        context.patchState({
            searchMode: action.searchMode
        });
    }
    setViewMode(context, action) {
        context.patchState({
            viewMode: action.viewMode
        });
    }
    setSort(context, action) {
        const searchResults = context.getState().searchResults;
        context.patchState({
            sort: action.sort,
            searchResults: [...searchResults]
        });
    }
    getStudioCategories(context, { organizationId }) {
        // Get biodigital categories
        const categories$ = of(exploreMenu.categories); // title, tag
        // Get organization collections
        const collections$ = organizationId
            ? this.organizationsService.getCollections(organizationId)
            : of([]);
        // Retrieve and combine various options
        forkJoin(categories$, collections$)
            .subscribe(([categories, collections]) => {
            let options = [];
            // Assemble various options
            options = [
                new StudioOptionsModel('My Models', '', 'user'),
                new StudioOptionsModel('Biodigital Models', '', 'category'),
                ...categories.map((category) => {
                    return new StudioOptionsModel(category.title, category.tag, 'category');
                })
            ];
            // assemble organization related options
            if (organizationId) {
                options = [
                    ...options,
                    // add organization level options
                    new StudioOptionsModel('Team Models', '', 'organization', organizationId),
                    // add collection level options
                    ...collections.map((collection) => {
                        return new StudioOptionsModel(collection.collection_name, collection.collection_id.toString(), 'collection', organizationId);
                    })
                ];
            }
            context.dispatch(new GalleryActions.GetStudioOptionsSuccess(options));
        });
    }
    getStudioOptionsSuccess(context, action) {
        context.patchState({
            studioOptions: [...action.options]
        });
    }
    searchStudio(context, action) {
        const { category, query } = action;
        const { organizationId, value, id, type } = category;
        context.patchState({
            loading: true,
            studioQuery: query,
            studioCategoryId: id
        });
        let search$ = new Observable();
        // Search module services
        switch (type) {
            case 'category':
                search$ = (value === 'public') ?
                    this.bookmarksService.getPublicBookmarks(query) :
                    this.exploreService.getAnatomyCategory(query, value.toString());
                break;
            case 'organization':
                search$ = this.bookmarksService.getOrganizationContent(organizationId, query);
                break;
            case 'collection':
                search$ = this.organizationsService.getCollectionContent(organizationId, value, query);
                break;
            case 'user':
                search$ = this.bookmarksService.getPrivateBookmarks(query);
                break;
        }
        search$
            .pipe(finalize(() => context.patchState({ loading: false })))
            .subscribe(results => {
            context.dispatch(new GalleryActions.SearchStudioSuccess(results));
        });
    }
    searchStudioSuccess(context, action) {
        context.dispatch(new GalleryActions.SearchSuccess(action.bookmarks));
    }
    searchLoft(context, action) {
        context.patchState({ loading: true });
        const isExplicit = action.name.indexOf('.json') > 0;
        // Search module services
        this.modulesService.listContents(action.primaryCategory, action.secondaryCategory, (isExplicit) ? action.name : '')
            .pipe(map((results) => {
            if (!isExplicit) {
                return results.filter(result => result.name.indexOf(action.name) >= 0);
            }
            else {
                return results;
            }
        }), finalize(() => context.patchState({ loading: false })))
            .subscribe(results => {
            context.dispatch(new GalleryActions.SearchLoftSuccess(results));
        }, (err) => {
            context.dispatch(new GalleryActions.SearchLoftSuccess([]));
        });
    }
    searchLoftSuccess(context, action) {
        const results = action.bookmarks.map(value => {
            // For loft content, ID and Label both just use filename.
            // For label, strip off the leading directories for easier parsing. 
            const id = value.name
                .replace('/content/modules/', '')
                .replace('.json', '');
            const title = value.name
                .split('/')
                .pop()
                .replace('/content/modules', '')
                .replace('.json', '');
            const bookmark = new BookmarkModel();
            bookmark.id = id;
            bookmark.title = title;
            bookmark.type = StoryType.Module;
            return bookmark;
        });
        context.dispatch(new GalleryActions.SearchSuccess(results));
    }
    searchSuccess(context, action) {
        context.patchState({
            searchResults: action.bookmarks
        });
    }
    clearResults(context, action) {
        context.patchState({
            searchResults: []
        });
    }
    resetGallery(context, action) {
        context.patchState(initialState);
    }
};
__decorate([
    Action(GalleryActions.SetSearchMode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SetSearchMode]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "setSearchMode", null);
__decorate([
    Action(GalleryActions.SetViewMode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SetViewMode]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "setViewMode", null);
__decorate([
    Action(GalleryActions.SetSort),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SetSort]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "setSort", null);
__decorate([
    Action(GalleryActions.GetStudioOptions),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.GetStudioOptions]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "getStudioCategories", null);
__decorate([
    Action(GalleryActions.GetStudioOptionsSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.GetStudioOptionsSuccess]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "getStudioOptionsSuccess", null);
__decorate([
    Action(GalleryActions.SearchStudio),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SearchStudio]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "searchStudio", null);
__decorate([
    Action(GalleryActions.SearchStudioSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SearchStudioSuccess]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "searchStudioSuccess", null);
__decorate([
    Action(GalleryActions.SearchLoft),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SearchLoft]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "searchLoft", null);
__decorate([
    Action(GalleryActions.SearchLoftSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SearchLoftSuccess]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "searchLoftSuccess", null);
__decorate([
    Action(GalleryActions.SearchSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.SearchSuccess]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "searchSuccess", null);
__decorate([
    Action(GalleryActions.ClearResults),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.ClearResults]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "clearResults", null);
__decorate([
    Action(GalleryActions.Reset),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, GalleryActions.Reset]),
    __metadata("design:returntype", void 0)
], GalleryState.prototype, "resetGallery", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getLoading", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getSearchMode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getViewMode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getSort", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getSortOptions", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getStudioOptions", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getSearchResults", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getStudioCategoryId", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GalleryStateModel]),
    __metadata("design:returntype", void 0)
], GalleryState, "getStudioQuery", null);
GalleryState = __decorate([
    State({
        name: 'gallery',
        defaults: initialState
    }),
    __metadata("design:paramtypes", [OrganizationsService,
        ModulesService,
        ExploreService,
        BookmarksService])
], GalleryState);
export { GalleryState };
